import _ from 'lodash';
import { addSequenceItemsToQueryResult } from './query';
import { getApolloClientSingleton } from 'lib/apollo/client/browser';
import { FIRST_PAGE_VAR } from '../components/AnnouncementStreamContainer';
import GET_ANNOUNCEMENT_STREAM_ITEMS from '../queries/GetAnnouncementStreamItems.gql';
import type {
  GetAnnouncementStreamItems,
  GetAnnouncementStreamItemsVariables,
} from '../queries/__generated__/GetAnnouncementStreamItems';
import ENTITY_STREAM_PREVIEW_FRAGMENT from '../queries/EntityStreamPreviewFragment.gql';
import type { EntityStreamPreviewFragment } from '../queries/__generated__/EntityStreamPreviewFragment';
import type { SequenceItemFragment } from 'chat/components/Stream/fragments/__generated__/SequenceItemFragment';

export function updateEntityStreamPreview(uuid: string, date: Date, preview: string) {
  const client = getApolloClientSingleton();

  client.writeFragment<EntityStreamPreviewFragment>({
    id: client.cache.identify({ __typename: 'EntityStream', uuid }),
    fragment: ENTITY_STREAM_PREVIEW_FRAGMENT,
    data: {
      __typename: 'EntityStream',
      uuid,
      preview: {
        __typename: 'StreamPreview',
        preview,
        sentAt: date.toISOString(),
      },
    },
  });
}

export async function addAnnouncementStreamSequenceItem(
  streamUUID: string,
  item: SequenceItemFragment,
  options: {
    overrideSequenceNumber?: boolean;
  } = {}
) {
  const client = getApolloClientSingleton();
  let result: GetAnnouncementStreamItems;

  try {
    // let's query for annoucnement streams, if the stream has previouslyt beeing fetched
    // then  apollo will return the cached value, otherwise we need to fetch it for the first time,
    // BUT, the new message wont be there (eventual consistency) so we might as well add it,
    // if it is duplicated then memory.ts configuration should remove the duplicate
    const { data, error } = await client.query<
      GetAnnouncementStreamItems,
      GetAnnouncementStreamItemsVariables
    >({
      query: GET_ANNOUNCEMENT_STREAM_ITEMS,
      variables: { uuid: streamUUID, ...FIRST_PAGE_VAR },
    });

    if (error) {
      throw error;
    }

    result = data;
  } catch (e) {
    console.error('addAnnouncementStreamSequenceItem error while querying announcement streams', e);
    return;
  }

  const message = options.overrideSequenceNumber
    ? {
        ...item,
        seq: `${lastMessageItemSequenceNumber(streamUUID) + 1}`,
      }
    : item;

  const newData = addSequenceItemsToQueryResult(_.cloneDeep(result), [message]);
  client.writeQuery<GetAnnouncementStreamItems, GetAnnouncementStreamItemsVariables>({
    query: GET_ANNOUNCEMENT_STREAM_ITEMS,
    variables: { uuid: streamUUID, ...FIRST_PAGE_VAR },
    data: newData,
  });

  if ('preview' in item.item) {
    updateEntityStreamPreview(streamUUID, new Date(), item.item.preview);
  }
}

export function lastMessageItemSequenceNumber(uuid: string) {
  let result: GetAnnouncementStreamItems | null;

  try {
    result = getApolloClientSingleton().readQuery<
      GetAnnouncementStreamItems,
      GetAnnouncementStreamItemsVariables
    >({
      query: GET_ANNOUNCEMENT_STREAM_ITEMS,
      variables: { uuid, ...FIRST_PAGE_VAR },
    });
  } catch (e) {
    return 0;
  }

  return Number(result?.entityStreams?.[0].sequenceItems[0]?.seq || 0);
}
